import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { Icon } from '~/components/Icon/Icon'
import { ToggleButton } from '~/components/ToggleButton/ToggleButton'
import { useCookies } from '~/features/cookies'
import styles from './CookiePreferenceOption.module.scss'
import type { CookiePreferenceOptionProps } from './CookiePreferences.interface'

export const CookiePreferenceOptionContext: FC<CookiePreferenceOptionProps> = ({
  name,
  isNecessary,
  title,
  description
}) => {
  const { hasPreference, preferenceIsSet, setPreference } = useCookies()
  const [isChecked, setIsChecked] = useState(false)

  const togglePreferenceCookie = (e: boolean): void => {
    const checked = isNecessary || e

    setPreference(name, checked)

    setIsChecked(checked)
  }

  const id = `cookie-pref-${name}`

  useEffect(() => {
    setIsChecked(hasPreference(name))
    const isSet = preferenceIsSet(name)

    if (isNecessary && !isSet) {
      // The visitor implicitly opts into any necessary cookies

      setPreference(name, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <CookiePreferenceOption
      key={name}
      name={name}
      isNecessary={isNecessary}
      title={title}
      description={description}
      togglePreferenceCookie={togglePreferenceCookie}
      isChecked={isChecked}
      id={id}
    />
  )
}

export const CookiePreferenceOption: FC<CookiePreferenceOptionProps> = ({
  name,
  isNecessary,
  title,
  description,
  togglePreferenceCookie,
  isChecked,
  id
}) => (
  <div className={styles.container}>
    <h3 className={styles.title}>{title}</h3>
    <p className={styles.description}>{description}</p>

    {isNecessary && (
      <div className={styles.input}>
        <div className={styles.tick}>
          <Icon name="#tick" className={styles.icon} />
        </div>
        <span className={styles['tick-label']}>Always active</span>
      </div>
    )}

    {!isNecessary && (
      <div className={styles.input}>
        <div className={styles.toggle}>
          <label htmlFor={`checkbox_${id}`} className={styles['toggle-label']}>
            {title}
          </label>
          <ToggleButton
            id={id}
            setChecked={togglePreferenceCookie}
            checked={isChecked ?? false}
            checkedText="On"
            uncheckedText="Off"
            width="7rem"
          />
        </div>
      </div>
    )}
  </div>
)
